<template>
	<div class="rte-toolbar">
		<div class="button-group">
			<div
				v-for="action in actions"
				:class="`rte-toolbar__formatting-action rte-toolbar__${ action.id }`"
				:key="action.id"
			>
				<TeamingToolbarButton
					:leftIcon="action.icon"
					:disabled="!action.enabled || disableAll"
					:active="!!action.active"
					@buttonPress="action.enabled ? onActionInvoke( action.id ) : noop"
				/>
			</div>
		</div>
		<ToolbarSpacer class="rte-toolbar-spacer" />
		<div class="button-group">
			<div
				v-for="teamingAction in teamingActions"
				:class="`rte-toolbar__formatting-action rte-toolbar__${ teamingAction.id }`"
				:key="teamingAction.id"
			>
				<TeamingToolbarButton
					:leftIcon="teamingAction.icon"
					:disabled="!teamingAction.enabled || disableAll"
					:active="teamingAction.active"
					@buttonPress="teamingAction.enabled ? onActionInvoke( teamingAction.id ) : noop"
				>
					<div :class="computeActionClasses( teamingAction.id )">
						{{ teamingAction.label }}
					</div>
				</TeamingToolbarButton>
			</div>
		</div>
	</div>
</template>

<script>
import {
	TeamingTiptapExtensionType,
	TiptapNativeNodeType
} from "@teaming/common-enums";

import {
	TeamingToolbarButton,
	ToolbarSpacer
} from "@teaming/common-ui";

export default {
	name: "RteToolbar",
	components: {
		TeamingToolbarButton,
		ToolbarSpacer,
	},
	emits: [ "editAction", ],
	inject: [
		"config",
	],
	props: {
		disableAll: {
			type: Boolean,
			default: false,
		},
		hasSelection: {
			type: Boolean,
			default: false,
		},
		allowedTeamingActions: {
			type: Array,
			default: () => [],
		},
		activeNodeType: {
			type: String,
			validator( value ) {
				const allTheTypes = [
					...Object.keys( TeamingTiptapExtensionType ),
					...Object.keys( TiptapNativeNodeType ),
				];
				return !value || allTheTypes.indexOf( value ) !== -1;
			},
			default: undefined,
		},
	},
	computed: {
		actions() {
			const canPerformAction = !this.activeNodeType ||
				( this.activeNodeType === TiptapNativeNodeType.HEADING );

			return [
				{
					id: "bold",
					icon: [ "fas", "bold", ],
					enabled: canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "italic",
					icon: [ "fas", "italic", ],
					enabled: canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "underline",
					icon: [ "fas", "underline", ],
					enabled: canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "heading1",
					icon: [ "fas", "heading", ],
					enabled: canPerformAction,
					active: this.activeNodeType === TiptapNativeNodeType.HEADING,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "list-ul",
					icon: [ "fas", "list-ul", ],
					enabled: canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "list-ol",
					icon: [ "fas", "list-ol", ],
					enabled: canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
				},
				{
					id: "link",
					icon: [ "fas", "link", ],
					enabled: this.hasSelection && canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": !this.hasSelection,
					},
				},
				{
					id: "image",
					icon: [ "fas", "image", ],
					enabled: !this.hasSelection && canPerformAction,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": !this.hasSelection,
					},
				},
			];
		},
		teamingActions() {
			const teamingActions = [];
			if ( this.allowedTeamingActions.includes( "topic" ) ) {
				teamingActions.push( {
					id: "topic",
					icon: [ "fas", "comment-alt-lines", ],
					enabled: true,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
					label: this.$t( "common.TeamingToolbarButton_topicLabel" ),
					active: this.activeNodeType === TeamingTiptapExtensionType.TOPIC,
				} );
			}
			if ( this.allowedTeamingActions.includes( "commitment" ) ) {
				teamingActions.push( {
					id: "commitment",
					icon: [ "fas", "check-square", ],
					enabled: true,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
					label: this.$t( "common.TeamingToolbarButton_commitmentLabel" ),
					active: this.activeNodeType === TeamingTiptapExtensionType.COMMITMENT,
				} );
			}
			if ( this.allowedTeamingActions.includes( "decision" ) ) {
				teamingActions.push( {
					id: "decision",
					icon: [ "fas", "gavel", ],
					enabled: true,
					classes: {
						"rte-toolbar__icon": true,
						"rte-toolbar__icon--disabled": false,
					},
					label: this.$t( "common.TeamingToolbarButton_decisionLabel" ),
					active: this.activeNodeType === TeamingTiptapExtensionType.DECISION,
				} );
			}
			return teamingActions;
		},
	},
	methods: {
		noop() {},
		computeActionClasses( id ) {
			return {
				"rte-toolbar__formatting-action-label": true,
				"rte-toolbar__formatting-action-label--hideable": id !== "topic",
				"rte-toolbar__formatting-action-label--partially-hideable": id === "topic",
			};
		},
		onActionInvoke( action ) {
			this.$emit( "editAction", action );
		},
	},
};
</script>

<style lang="scss" scoped>
$rte-toolbar-icon-color: $color1-500;
$rte-toolbar-icon-color--disabled: $color1-200;
$rte-toolbar-icon-background--hover: $color1-100;
$rte-toolbar-button-divider: $color1-200;

.rte-toolbar {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.25rem;
}

.rte-toolbar-spacer {
	width: 1rem;
}

.rte-toolbar > .button-group {
	display: flex;
	flex-direction: row;
}

.rte-toolbar > .divider-group {
	color: $borderColor--dark;
	padding: 0 0.25rem;

	@include for-size('md') {
		padding: 0 0.5rem;
	}
}

.rte-toolbar__formatting-action {
	margin-right: 0.3rem;
}

.rte-toolbar__icon {
	color: $rte-toolbar-icon-color;
}

.rte-toolbar__icon--disabled {
	color: $rte-toolbar-icon-color--disabled;
}

.rte-toolbar__formatting-action-label {
	display: none;
	white-space: nowrap;
	padding-left: 0.475rem;

	@include font-and-line-for(0);
	font-style: normal;
	font-weight: 600;

	@include for-size('sm') {
		display: block;
	}
}

.rte-toolbar__formatting-action-label--hideable {
	display: none;

	@include for-size('sm') {
		display: block;
	}
}

.rte-toolbar__formatting-action-label--partially-hideable {
	@include for-size('xs') {
		display: none;
	}
}
</style>

<style lang="scss">
.rte-toolbar__topic {
	.teaming-toolbar-button__label-content {
		// force topic button to always display text
		@include for-size-and-smaller('sm') {
			display: block;
		}
	}
}
</style>
